import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap, share } from 'rxjs/operators';

interface CacheEntry {
  response: HttpResponse<any>;
  expiry: number;
}

/**
 * HTTP GET caching interceptor.
 *
 * Caches responses for read-only customer-data endpoints so that multiple
 * components initialising in the same navigation event do not fan-out
 * duplicate network requests for the same data within the configured TTL.
 *
 * Default TTL per endpoint (ms):
 *   api/Customer/getMinimalProfileData   → 60 s
 *   api/Customer/GetPlansDetails         → 60 s
 *   api/system/resource-permission-detail→ 300 s
 *
 * The cache is in-memory and is cleared automatically when the entry expires.
 * Mutation requests (POST / PUT / PATCH / DELETE) are never cached.
 */
@Injectable()
export class CacheInterceptor implements HttpInterceptor {
  private readonly cache = new Map<string, CacheEntry>();

  /** TTL rules: key = URL substring, value = TTL in ms */
  private readonly ttlRules: Array<{ pattern: string; ttl: number }> = [
    { pattern: 'api/Customer/getMinimalProfileData', ttl: 60_000 },
    { pattern: 'api/Customer/GetPlansDetails',       ttl: 60_000 },
    { pattern: 'api/system/resource-permission-detail', ttl: 300_000 },
  ];

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Only cache GET requests
    if (req.method !== 'GET') {
      return next.handle(req);
    }

    const ttl = this.getTtl(req.url);
    if (!ttl) {
      return next.handle(req);
    }

    const cacheKey = req.urlWithParams;
    const cached = this.cache.get(cacheKey);

    if (cached && Date.now() < cached.expiry) {
      // Return a cloned cached response synchronously
      return of(cached.response.clone());
    }

    // Remove stale entry if present
    if (cached) {
      this.cache.delete(cacheKey);
    }

    const shared = next.handle(req).pipe(
      tap((event) => {
        if (event instanceof HttpResponse && event.status === 200) {
          this.cache.set(cacheKey, {
            response: event.clone(),
            expiry: Date.now() + ttl,
          });
        }
      }),
      share()
    );

    return shared;
  }

  private getTtl(url: string): number {
    for (const rule of this.ttlRules) {
      if (url.includes(rule.pattern)) {
        return rule.ttl;
      }
    }
    return 0;
  }
}
